/* lib/academy/errorCodes.js — реестр доменных кодов ошибок «Академии».
 * Ключи ОБЯЗАНЫ байт-в-байт совпадать с кодами бэкенда (backend/academy/
 * services.py AcademyError(code=…), views.py _err(…, code), purchasing.py
 * PurchaseError(code=…)) — по образцу lib/reports/inventoryCriteria.js.
 *
 * Зачем: серверный `detail` написан для разработчика («Урок пока закрыт.») и
 * ничего не говорит человеку о том, ЧТО делать. Экраны ученика печатали его в
 * красный тост как есть. Здесь код превращается в пару «что случилось» +
 * «что делать»; текст сервера остаётся фолбэком для незнакомых кодов, чтобы
 * новый код на бэке не превращался в немую «Ошибку». */

export const ACADEMY_ERRORS = {
  // ── доступ к уроку (lesson_lock → `locked_{reason}`) ──────────────────────
  locked_drip: {
    title: 'Урок ещё не открылся',
    hint: 'Модули этого курса открываются по расписанию — вернитесь к уроку позже.',
  },
  locked_sequential: {
    title: 'Сначала пройдите предыдущие уроки',
    hint: 'Курс проходится по порядку: закройте обязательные уроки выше по программе.',
  },
  locked_tariff: {
    title: 'Урок не входит в ваш тариф',
    hint: 'Чтобы открыть этот модуль, перейдите на тариф с полной программой.',
  },
  locked_frozen: {
    title: 'Доступ к курсу заморожен',
    hint: 'Обучение на паузе — таймер доступа остановлен. Разморозку делает организатор обучения.',
  },
  // подписка Restorium кончилась: доступ, выданный ею, на ПАУЗЕ (не удалён)
  locked_subscription: {
    title: 'Подписка Restorium не активна',
    hint: 'Курс открыт по подписке — продлите её, и обучение продолжится с того же места: прогресс, сданные работы и баллы сохранены.',
  },
  // тариф «без ДЗ» (Tariff.homework_enabled=false): сдавать нечего и незачем
  homework_disabled: {
    title: 'На вашем тарифе нет домашних заданий',
    hint: 'Задания не нужны для прохождения курса и получения сертификата.',
  },
  // тот же смысл без причины (view/complete/quiz/homework отдают голый `locked`)
  locked: {
    title: 'Урок закрыт',
    hint: 'Откройте его по программе курса — слева видно, какие уроки уже доступны.',
  },
  frozen: {
    title: 'Доступ к курсу заморожен',
    hint: 'Обучение на паузе — уроки недоступны, пока организатор не снимет заморозку.',
  },

  // ── тест ─────────────────────────────────────────────────────────────────
  attempts_exhausted: {
    title: 'Попытки закончились',
    hint: 'Запросите пересдачу у куратора — он вернёт доступ к тесту.',
  },
  attempts_left: {
    title: 'Попытки ещё остались',
    hint: 'Пересдача не нужна — пройдите тест снова.',
  },
  // Куратору/методисту при сбросе попыток уже СДАННОГО теста (services.
  // reset_quiz_attempts). Обычно перехватывается подтверждением и повтором с
  // force=1, но если отказ доедет до тоста — он обязан объяснять последствие.
  quiz_passed: {
    title: 'Тест уже сдан',
    hint: 'Сброс отменит зачёт по уроку и пересчитает прогресс — подтвердите, если это нужно.',
  },

  // ── домашнее задание ─────────────────────────────────────────────────────
  feedback_closed: {
    title: 'Период обратной связи по тарифу завершён',
    hint: 'Сдать работу на проверку больше нельзя — обратитесь к куратору.',
  },
  already_accepted: {
    title: 'Задание уже принято',
    hint: 'Куратор засчитал эту работу — пересдавать её не нужно.',
  },
  in_review: {
    title: 'Работа уже на проверке',
    hint: 'Дождитесь вердикта куратора — новую попытку можно отправить после него.',
  },
  file_required: {
    title: 'Нужен файл',
    hint: 'По условиям задания к ответу обязательно приложить файл.',
  },
  empty: {
    title: 'Ответ пустой',
    hint: 'Опишите выполнение задания или приложите файл.',
  },
  bad_file: {
    title: 'Файл не подходит',
    hint: 'Проверьте размер и тип файла — принимаются документы, архивы и изображения.',
  },
  requirements_not_met: {
    title: 'Урок ещё не выполнен',
    hint: 'Сдайте тест и дождитесь проверки задания — после этого урок закроется сам.',
  },

  // ── запись и доступ к курсу ──────────────────────────────────────────────
  not_enrolled: {
    title: 'Вы не записаны на курс',
    hint: 'Откройте страницу курса и запишитесь — прогресс сохранится с первого урока.',
  },
  invite_only: {
    title: 'На курс записывает методист',
    hint: 'Оставьте заявку на странице курса — вас запишут и пришлют доступ.',
  },
  already_enrolled: {
    title: 'Доступ к курсу уже есть',
    hint: 'Курс открыт в разделе «Обучение» — платить второй раз не нужно.',
  },
  payment_required: {
    title: 'Курс платный',
    hint: 'Оформите доступ на странице курса, чтобы начать обучение.',
  },
  not_published: {
    title: 'Курс недоступен',
    hint: 'Он снят с публикации — уточните у организатора обучения.',
  },
  disabled: {
    title: 'Раздел обучения недоступен',
    hint: 'Академия временно отключена — попробуйте позже.',
  },

  // ── оплата ───────────────────────────────────────────────────────────────
  consent_required: {
    title: 'Нужно принять оферту и политику',
    hint: 'Отметьте оба согласия в форме оформления — без них оплата не начнётся.',
  },
  bad_price: {
    title: 'Цена курса не задана',
    hint: 'Оплата пока невозможна — сообщите об этом организатору обучения.',
  },
  tariff_required: {
    title: 'Не выбран тариф',
    hint: 'Курс продаётся по тарифам — выберите подходящий в форме оформления.',
  },
  tariff_unavailable: {
    title: 'Тариф недоступен',
    hint: 'Он снят с продажи — выберите другой в форме оформления.',
  },
  not_paid: {
    title: 'Курс не продаётся',
    hint: 'Этот курс нельзя купить — доступ выдаёт организатор обучения.',
  },
  rate_limited: {
    title: 'Слишком много попыток',
    hint: 'Подождите немного и повторите — заявки принимаются с ограничением по времени.',
  },

  // ── удаление курса/тарифа (методист) ─────────────────────────────────────
  /* Оплаты (`CoursePurchase.tariff`) и доступы учеников (`EnrollmentTariff.
     tariff`) стоят на PROTECT: платёжная запись и доступ не исчезают вместе с
     курсом. Раньше каскад ронял ProtectedError → HTTP 500, и «Удалить» в
     консоли отвечало немым красным тостом. Отказ обязан называть СЛЕДУЮЩИЙ ШАГ,
     а он разный: курс — заархивировать (статус «В архиве» в том же экране),
     тариф — деактивировать (галочка «Активен»). Поэтому коды РАЗНЫЕ: по коду
     видно и причину, и что предлагать, а `?force=1` тут не пропуск в принципе. */
  has_purchases: {
    title: 'По курсу есть оплаты',
    hint: 'Платёжные записи не удаляются вместе с курсом — заархивируйте его: статус «В архиве» убирает курс из каталога и из списка курсов.',
  },
  has_tariff_enrollments: {
    title: 'На курс записаны ученики по тарифам',
    hint: 'Доступ ученика ссылается на тариф, поэтому курс можно только заархивировать — поставьте статус «В архиве».',
  },
  tariff_purchased: {
    title: 'Тариф уже купили',
    hint: 'История оплат неизменна — деактивируйте тариф: снимите «Активен», и он перестанет продаваться.',
  },
  /* `has_enrollments` и `protected` здесь СОЗНАТЕЛЬНО отсутствуют: один и тот же
     код приходит и от удаления курса, и от удаления тарифа, а выход из тупика у
     них разный. `academyErrorText` предпочитает реестр серверному `detail`, так
     что общая запись ЗАМЕНИЛА БЫ точный текст бэкенда на размытый. Оба detail
     написаны готовыми к показу — печатаем их как есть. */
};

/** Описание кода: {code, title, hint} или null для незнакомого/пустого кода. */
export function academyError(err) {
  const code = err && typeof err === 'object' ? err.code : err;
  if (!code || !ACADEMY_ERRORS[code]) return null;
  return { code, ...ACADEMY_ERRORS[code] };
}

/** Текст ошибки для тоста: «Что случилось. Что делать».
 *  Незнакомый код → текст сервера (detail/message), затем `fallback`. */
export function academyErrorText(err, fallback = 'Не удалось выполнить действие') {
  const known = academyError(err);
  if (known) return known.hint ? `${known.title}. ${known.hint}` : known.title;
  const raw = err && typeof err === 'object' ? (err.detail || err.message) : null;
  return raw || fallback;
}

// Иконка экрана-заглушки урока по причине отказа. Пауза — «pause», расписание —
// «clock», всё остальное — замок: глиф не должен противоречить тексту.
const LESSON_LOCK_ICON = {
  locked_frozen: 'pause', frozen: 'pause',
  locked_drip: 'clock',
  not_enrolled: 'book', payment_required: 'credit-card',
  homework_disabled: 'info',
  locked_subscription: 'credit-card',
};

/** Экран урока по отказу 403: {code, title, body, icon, frozen} или null.
 *
 *  Зачем отдельная функция: плеер сваливал ЛЮБОЙ 403 в единственную известную
 *  ему причину — «Доступ заморожен» — и обещал ученику продление срока на время
 *  паузы. Ученик с последовательным локом (паузы нет, лечится прохождением
 *  предыдущего урока) читал про несуществующую паузу. Реестр причин при этом
 *  существовал и на этом пути не использовался.
 *
 *  Незнакомый/пустой код НЕ трактуем как заморозку: общий `locked` («Урок
 *  закрыт») ничего лишнего не обещает, а заморозка — состояние всего курса,
 *  называть его по догадке нельзя. */
export function lessonAccessNotice(err) {
  if (!err || typeof err !== 'object' || err.status !== 403) return null;
  const code = (err.code && ACADEMY_ERRORS[err.code]) ? err.code : 'locked';
  const d = ACADEMY_ERRORS[code];
  return {
    code,
    title: d.title,
    body: d.hint,
    icon: LESSON_LOCK_ICON[code] || 'lock',
    frozen: code === 'locked_frozen' || code === 'frozen',
  };
}
